import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';

function ExamCentresView() {
  const [examCentres, setExamCentres] = useState([]);
  const [allCentres, setAllCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [confirmDeleteCentre, setConfirmDeleteCentre] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchExamCentres();
  }, []);

  const fetchExamCentres = () => {
    setLoading(true);
    axios.get('/api/exam-centres')
      .then(response => {
        const sorted = response.data.sort((a, b) => b.id - a.id);
        setExamCentres(sorted);
        setAllCentres(sorted);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching exam centres.');
        setLoading(false);
      });
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const sortedCentres = [...examCentres].sort((a, b) => {
    const aVal = a[sortKey] || '';
    const bVal = b[sortKey] || '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    } else {
      return sortOrder === 'asc'
        ? aVal.toString().localeCompare(bVal.toString())
        : bVal.toString().localeCompare(aVal.toString());
    }
  });

  const handleEdit = (id) => {
    navigate(`/admin/exam-centres/edit/${id}`);
  };

  const handleDelete = (id, canDelete) => {
    if (canDelete === false) {
      setTooltip('This exam centre cannot be deleted.');
      return;
    }
    setConfirmDeleteCentre({ id, canDelete });
  };

  const confirmDeletion = async () => {
    try {
      await axios.delete(`/api/exam-centres/${confirmDeleteCentre.id}`);
      setTooltip('Exam centre deleted successfully!');
      fetchExamCentres();
      setConfirmDeleteCentre(null);
    } catch (err) {
      setTooltip('Error deleting exam centre.');
      setConfirmDeleteCentre(null);
    }
  };

  const cancelDeletion = () => {
    setConfirmDeleteCentre(null);
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase().trim();
    setSearchQuery(query);
    if (!query) {
      setExamCentres(allCentres);
    } else {
      const filtered = allCentres.filter(centre =>
        (centre.centreName ? centre.centreName.toLowerCase().includes(query) : false) ||
        (centre.location ? centre.location.toLowerCase().includes(query) : false) ||
        (centre.centreNumber ? centre.centreNumber.toString().toLowerCase().includes(query) : false)
      );
      setExamCentres(filtered);
    }
  };

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'centreName', header: <>Centre Name</> },
    { key: 'location', header: <>Location</> },
    { key: 'centreNumber', header: <>Centre Number</> },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (centre, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td>{centre.centreName}</td>
      <td>{centre.location}</td>
      <td>{centre.centreNumber}</td>
      <td>
        <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(centre.id)}>
          Edit
        </button>
        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(centre.id, centre.canDelete)}>
          Delete
        </button>
      </td>
    </>
  );

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="View All Exam Centres" />
      <div className="mb-3">
        <input 
          type="text" 
          className="form-control" 
          placeholder="Search exam centres..." 
          value={searchQuery}
          onChange={handleSearch}
          style={{ maxWidth: '300px' }}
        />
      </div>
      {loading ? (
        <p>Loading exam centres...</p>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : examCentres.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={sortedCentres}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {confirmDeleteCentre && (
        <ConfirmationModal 
          title="Confirm Delete"
          message="Are you sure you want to delete this exam centre?"
          onConfirm={confirmDeletion}
          onCancel={cancelDeletion}
        />
      )}
    </div>
  );
}

export default ExamCentresView;
